import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import {
  Typography,
  Grid,
  Box,
  Paper,
  LinearProgress,
} from '@material-ui/core';
import { Rating } from '@material-ui/lab';
import { makeStyles, withStyles } from '@material-ui/core/styles';

import {
  Star as StarIcon,
} from '@material-ui/icons';

import {
  green,
  yellow,
  red,
  blue,
  purple,
  grey,
} from '@material-ui/core/colors';

import {
  getReviewSummary,
  selectGuest,
  selectReviewSummary,
} from '../../store/widgetSlice';

import {
  setMessage,
} from '../../store/authSlice';

const useStyles = makeStyles((theme) => ({
  container: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    maxWidth: '100%',
  },
  table: {
    marginLeft: theme.spacing(4),
    marginRight: theme.spacing(4),
    width: `calc(100% - ${theme.spacing(8)}px)`,
  },
  row: {
    justifyContent: 'space-between',
  },
  cell: {
    padding: 0,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),

    'tr:first-child &': {
      borderTop: '1px solid rgba(224, 224, 224, 1)',
    },

    'tr:last-child &': {
      borderBottom: 0,
    },
  },
  panel: {
    backgroundColor: theme.palette.background.default,
    minWidth: 550,
  },
  heading: {
    fontSize: 14,
    fontWeight: 600,
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    alignSelf: 'top',
  },
  cardHeading: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  name: {
    fontSize: 18,
    fontWeight: 600,
    textAlign: 'center',
  },
  fiveStarBar: {
    '& .MuiLinearProgress-barColorPrimary': {
      backgroundColor: green[500],
    },
  },
  fourStarBar: {
    '& .MuiLinearProgress-barColorPrimary': {
      backgroundColor: blue[500],
    },
  },
  threeStarBar: {
    '& .MuiLinearProgress-barColorPrimary': {
      backgroundColor: yellow[500],
    },
  },
  twoStarBar: {
    '& .MuiLinearProgress-barColorPrimary': {
      backgroundColor: purple[500],
    },
  },
  oneStarBar: {
    '& .MuiLinearProgress-barColorPrimary': {
      backgroundColor: red[500],
    },
  },
  rating: {
    fontSize: 26,
    fontWeight: 600,
    margin: 0,
    textAlign: 'center',
  },
  totalRating: {
    marginLeft: 2,
    color: grey[500],
    fontSize: 21,
    fontWeight: 600,
  },
  ratingClassContainer: {
    minWidth: 0,
  },
  equalHeight: {
    height: '100%',
  },
  ratingBox: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    justifyContent: 'space-between',
  },
  numberOfReviews: {
    fontSize: 14,
    fontWeight: 600,
    padding: 0,
  },
  ratingBars: {
    marginLeft: theme.spacing(4),
    marginRight: theme.spacing(4),
    paddingBottom: theme.spacing(1),
  },
  tableIcon: {
    verticalAlign: 'middle',
  },
}));

function LinearProgressWithLabel({
  className, amount, total, classes,
}) {
  return (
    <Box display="flex" alignItems="center">
      <Box display="flex" mr={1}>
        <StarIcon fontSize="small" style={{ color: '#ffb400' }} />
      </Box>
      <Box width="100%" mr={1}>
        <LinearProgress
          className={className}
          value={total ? (amount / total) * 100 : 0}
          variant="determinate"
          classes={classes}
        />
      </Box>
      <Box display="flex" minWidth={20}>
        <Typography variant="caption" color="textSecondary">
          {Math.round(amount) || 0}
        </Typography>
      </Box>
    </Box>
  );
}

const RatingBar = withStyles(() => ({
  root: {
    height: 5,
  },
  colorPrimary: {
    backgroundColor: grey[300],
  },
}))(LinearProgressWithLabel);

export default function ReviewSummary({ open }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const guest = useSelector(selectGuest);
  const summary = useSelector(selectReviewSummary);

  useEffect(() => {
    if (open && guest && guest.id) {
      dispatch(getReviewSummary({ guestId: guest.id }))
        .unwrap()
        .then(() => {
          dispatch(setMessage({ message: 'Review summary retrieved', severity: 'success' }));
        })
        .catch(() => {
          dispatch(setMessage({ message: 'Review summary could not be retrieved', severity: 'error' }));
        });
    }
  }, [dispatch, guest, open]);

  return (
    <>
      <Grid className={classes.ratingClassContainer} container>
        <Grid item xs="5">
          <Box className={classes.equalHeight} mr={2}>
            <Paper className={classes.ratingBox}>
              <Typography align="center" className={[classes.heading, classes.cardHeading]} component="h3">
                Overall Rating
              </Typography>
              <Box mb={2}>
                <p align="center" className={classes.rating}>
                  {(summary && summary.ratingAverage
                  && !Number.isNaN(summary.ratingAverage)
                  && summary.ratingAverage.toFixed(2)) || 0}
                  <span className={classes.totalRating}>/5</span>
                </p>
                <div align="center">
                  <Rating
                    value={summary && summary.ratingAverage}
                    precision={0.5}
                    readOnly
                  />
                </div>
                <Typography align="center" className={[classes.numberOfReviews]} component="p">
                  {(summary && summary.totalReviews) || 0}
                  {' '}
                  reviews
                </Typography>
              </Box>
            </Paper>
          </Box>
        </Grid>
        <Grid item xs="7">
          <Box ml={2}>
            <Paper className={classes.equalHeight}>
              <Typography className={[classes.heading, classes.cardHeading]} component="h3">
                Rating Breakdown
              </Typography>
              <Box className={classes.ratingBars}>
                <RatingBar
                  className={classes.fiveStarBar}
                  amount={summary && summary.ratedFive}
                  total={summary && summary.totalReviews}
                />
                <RatingBar
                  className={classes.fourStarBar}
                  amount={summary && summary.ratedFour}
                  total={summary && summary.totalReviews}
                />
                <RatingBar
                  className={classes.threeStarBar}
                  amount={summary && summary.ratedThree}
                  total={summary && summary.totalReviews}
                />
                <RatingBar
                  className={classes.twoStarBar}
                  amount={summary && summary.ratedTwo}
                  total={summary && summary.totalReviews}
                />
                <RatingBar
                  className={classes.oneStarBar}
                  amount={summary && summary.ratedOne}
                  total={summary && summary.totalReviews}
                />
              </Box>
            </Paper>
          </Box>
        </Grid>
      </Grid>
    </>
  );
}
